import { Flex, FormControl, FormLabel, Stack, Grid, GridItem, Tooltip } from "@chakra-ui/react";
import {
  Box,
  Button,
  InlineNotification,
  Input,
  Text,
  useToast,
  useTranslation,
  Tr,
  Td,
  Icon,
  SearchBox,
} from "@familyzone/component-library";

import React, { FC, useState, useMemo, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { Cohort, User, Group, Year } from "../../../types/Cohorts";
import { getCohort, updateCohort } from "../../../utils/api/Cohorts";
import TableBasedPage from "../../templates/TableBasedPage";
import CardSpinner from "../../reporting/UserDashboard/CardSpinner";
import CardBasedPage from "../../templates/CardBasedPage";
import { GroupUMS } from "../../../types/Groups";
import { subtract } from "../DiffSetHelper";
import { zIndices } from "../../../utils/ZIndexUtil";
import NuMultiSelect from "../../../utils/NuMultiSelect";
import { YEAR_LABELS } from "../sis-integration/AttributeConfig";
import { IdentitySearch, IdentitySearchParams } from "../IdentitySearch/IdentitySearch";
import { searchGroups } from "../../../utils/api/Groups";

interface CohortProps {
  params: {
    id: string;
  };
}

interface SelectOption<T> {
  value: T;
  text: string;
}

const SELECT_STYLE = `
.nuselect_selected {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.listselector-item {
  width: inherit !important;
  height: inherit !important;
  margin: inherit !important;
}

.nuselect_search {
  width: 300px;
}

.nuselect_search_selection {
  z-index: 20;
}
`;

const ManageCohort: FC<CohortProps> = ({ params }: CohortProps) => {
  const [selectedGroups, setSelectedGroups] = useState<{ groups: Map<string, Group> }>({ groups: new Map<string, Group>() });
  const [selectedGroupsSearch, setSelectedGroupsSearch] = useState("");
  const filteredSelectedGroups = useMemo(() => {
    if (selectedGroupsSearch) {
      return new Map(
        [...selectedGroups.groups.entries()].filter(([_key, value]) =>
          value.name.toLowerCase().includes(selectedGroupsSearch.toLowerCase())
        )
      );
    }
    return selectedGroups.groups;
  }, [selectedGroups.groups, selectedGroupsSearch]);
  const { id } = params;
  const [cohortDetails, setCohortDetails] = useState<Cohort>();
  const [selectedYears, setSelectedYears] = useState<Year[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [availableGroups, setAvailableGroups] = useState<GroupUMS[]>([]);
  const [saving, setSaving] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [, setLoading] = useState(true);
  const [hasAutoFocused, setHasAutoFocused] = useState(false);
  const GROUPS_PAGE_SIZE = 25;
  const MAX_TABLE_HEIGHT = "600px";

  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();

  const groupTableColumns = [
    { headerText: t("Group"), columnName: "name", sortable: true, searchable: true },
    { headerText: t("Provider"), columnName: "sourceType", sortable: true, searchable: false },
  ];

  const userTableColumns = [
    { headerText: t("Username"), columnName: "username", sortable: true, searchable: true },
    { headerText: t("Email"), columnName: "email", sortable: true, searchable: true },
    { headerText: t("First name"), columnName: "firstName", sortable: true, searchable: true },
    { headerText: t("Last name"), columnName: "lastName", sortable: true, searchable: true },
  ];

  const yearOptions: SelectOption<string>[] = Object.entries(YEAR_LABELS).map(([k, v]) => ({
    text: v,
    value: k,
  }));
  const buttonColumn = [{ headerText: "", columnName: "", sortable: false, searchable: false }];
  const visibleGroups = availableGroups.filter((v) => !selectedGroups.groups.has(v.id));

  const search = useCallback((params: IdentitySearchParams): Promise<GroupUMS[]> => {
    return searchGroups(params.query, true, params.pageSize);
  }, []);

  const fetchCohortData = useCallback(async () => {
    try {
      setLoading(true);
      const cohort = await getCohort(id);
      if (cohort.members) setFilteredUsers(cohort.members.slice(0, 15));
      if (cohort.groups)
        setSelectedGroups({
          groups: new Map(
            cohort.groups
              .map((group) => ({
                id: group.id,
                name: group.name,
                sourceType: String(group.sourceType),
                active: group.active,
              }))
              .map((group) => [group.id, group])
          ),
        });
      setCohortDetails(cohort);
      setSelectedYears(cohort.attributes.years);
      setErrorState(false);
    } catch (_) {
      setErrorState(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchCohortData();
  }, [fetchCohortData]);

  useEffect(() => {
    if (cohortDetails && !hasAutoFocused) {
      setTimeout(() => {
        const nameInput = document.querySelector('input[aria-label="Name"]') as HTMLInputElement;
        if (nameInput && !nameInput.disabled) {
          nameInput.focus();
          setHasAutoFocused(true);
        }
      }, 100);
    }
  }, [cohortDetails, hasAutoFocused]);

  if (errorState)
    return (
      <Box m="sp24">
        <InlineNotification
          status="error"
          notificationTitle={t("Error loading cohort")}
          notificationDescription={t("There's a problem loading this cohort right now. Please try again later.")}
        />
      </Box>
    );

  if (!cohortDetails) return <CardSpinner />;

  const filterUsers = (value = "") => {
    if (!cohortDetails.members) return [];
    const searchTerm = value.toLowerCase();
    return cohortDetails.members
      .filter((member) => {
        const name = `${member.firstName ?? ""} ${member.lastName ?? ""}`.toLowerCase();
        const username = member.username;
        return name.includes(searchTerm) || username?.includes(searchTerm);
      })
      .slice(0, 15);
  };

  const handleSave = async () => {
    setSaving(true);

    const groupsToAdd = Array.from(
      subtract(new Set(selectedGroups.groups.keys()), new Set((cohortDetails.groups ?? []).map((group) => String(group.id))))
    );

    const groupsToRemove = Array.from(
      subtract(new Set((cohortDetails.groups ?? []).map((group) => String(group.id))), new Set(selectedGroups.groups.keys()))
    );

    const yearsToAdd: Year[] = Array.from(subtract(new Set(selectedYears), new Set(cohortDetails?.attributes?.years ?? [])));
    const yearsToRemove: Year[] = Array.from(subtract(new Set(cohortDetails?.attributes?.years ?? []), new Set(selectedYears)));

    try {
      await updateCohort(id, {
        name: cohortDetails.name,
        description: cohortDetails.description,
        groupIDsToAdd: groupsToAdd,
        groupIDsToRemove: groupsToRemove,
        attributesToAdd: {
          years: yearsToAdd,
        },
        attributesToRemove: {
          years: yearsToRemove,
        },
      });
      successToast({ title: t("Cohort saved successfully.") });
      await fetchCohortData();
    } catch (e) {
      errorToast({ title: t(String(e)) });
    } finally {
      setSaving(false);
    }
  };

  const handleAddGroup = (group: GroupUMS) => {
    // Add group to selected groups
    const newGroup: Group = {
      id: group.id,
      name: group.name,
      sourceType: String(group.sourceType),
    };

    setSelectedGroups((prev) => {
      prev.groups.set(group.id, newGroup);
      return {
        groups: prev.groups,
      };
    });
  };

  const handleRemoveGroup = (groupId: string) => {
    setSelectedGroups((prev) => {
      prev.groups.delete(groupId);
      return {
        groups: prev.groups,
      };
    });
  };

  const availableTableDataMap = (group: GroupUMS, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="group-name">
          <Text fontSize={"sm"}>{group.name}</Text>
        </Td>
        <Td data-testid="group-sourcetype">
          <Text>{group.sourceType}</Text>
        </Td>
        <Td data-testid="group-checkbox">
          <Button
            name={`group-${group.id}`}
            onClick={() => handleAddGroup(group)}
            aria-label={`${group.name}`}
            variant="outline"
            width="40px"
          >
            <Icon icon="fa-chevron-right" />
          </Button>
        </Td>
      </Tr>
    );
  };

  const availableUserTableDataMap = (user: User, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="user-email">
          <Link style={{ marginTop: "1px" }} to={`/config/device/userdb/users/qid/${user.qoriaUserID}`} key={index}>
            <Text as="span" pl="sp8" color="brand.500">
              {user.username}
            </Text>
          </Link>
        </Td>
        <Td data-testid="user-email">
          <Text fontSize={"sm"}>{user.email}</Text>
        </Td>
        <Td data-testid="user-first-name">
          <Text fontSize={"sm"}>{user.firstName}</Text>
        </Td>
        <Td data-testid="user-last-name">
          <Text fontSize={"sm"}>{user.lastName}</Text>
        </Td>
      </Tr>
    );
  };

  const selectedGroupsTableDataMap = (group: Group, index: number): JSX.Element => {
    const textColor = group.active === false ? "neutrals.60" : "neutrals.900";
    return (
      <Tr key={index}>
        <Td data-testid="group-checkbox">
          <Button
            name={`selected-group-${group.id}`}
            onClick={() => handleRemoveGroup(group.id)}
            aria-label={`${group.name}`}
            variant="outline"
            width="40px"
          >
            <Icon icon="fa-chevron-left" />
          </Button>
        </Td>
        <Td data-testid="group-name">
          <Text fontSize={"sm"} color={textColor}>
            {group.name}
          </Text>
        </Td>
        <Td data-testid="group-sourcetype">
          <Text color={textColor}>{group.sourceType}</Text>
        </Td>
      </Tr>
    );
  };

  const reservedCohort = cohortDetails.type !== "DEFAULT";
  const archivedCohort = cohortDetails.active === false;
  const minimumSearchLength = 3;

  const availableGroupHeader = (all: Array<GroupUMS>, visible: Array<GroupUMS>): JSX.Element => {
    if (all.length === GROUPS_PAGE_SIZE) {
      return (
        <Flex>
          <Tooltip hasArrow label="Not all groups displayed, search to refine results." variant="dark" placement="right">
            <span>
              <Text fontFamily="Poppins" fontSize="s" mb="sp8">
                {t("Available Groups")} ({visible.length} of many)
                <Icon icon="fa-circle-question" boxSize="1.25rem" marginRight="5px" marginLeft="5px" />
              </Text>
            </span>
          </Tooltip>
        </Flex>
      );
    }

    return (
      <Text fontFamily="Poppins" fontSize="s" mb="sp8">
        {t("Available Groups")} ({visible.length})
      </Text>
    );
  };

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Users and Groups"), url: "/config/device/userdb", isActive: false },
    { title: t("Cohorts"), url: "/config/device/userdb/cohorts", isActive: false },
    { title: t("Manage Cohort"), isActive: true },
  ];

  return (
    <CardBasedPage title={"Manage Cohort"} breadcrumbs={breadcrumbs}>
      <Box width="100%">
        <Box backgroundColor="neutrals.20" m="sp16" p="sp8">
          <Text fontFamily="Poppins" fontSize="xl">
            {t("Details")}
          </Text>
        </Box>
        <Stack spacing="sp12" m="sp16" p="sp8" width="100%">
          <Stack width="50%">
            {reservedCohort && (
              <Box>
                <InlineNotification
                  status="warning"
                  notificationTitle={t("Editing disabled")}
                  notificationDescription={t("This is a reserved cohort and the name cannot be edited.")}
                />
              </Box>
            )}
            {archivedCohort && (
              <Box>
                <InlineNotification
                  status="warning"
                  notificationTitle={t("Editing disabled")}
                  notificationDescription={t("This is an archived cohort and cannot be edited. Pls unarchive to make changes.")}
                />
              </Box>
            )}
            <FormControl>
              <Flex alignItems="center">
                <FormLabel minWidth="120px" mb="0">
                  {t("Name")}
                </FormLabel>
                <Input
                  aria-label="Name"
                  value={cohortDetails.name}
                  isDisabled={reservedCohort || archivedCohort}
                  onChange={(e) => setCohortDetails({ ...cohortDetails, name: e.target.value })}
                />
              </Flex>
            </FormControl>
            <FormControl>
              <Flex alignItems="center">
                <FormLabel minWidth="120px" mb="0">
                  {t("Description")}
                </FormLabel>
                <Input
                  aria-label="Description"
                  resize="vertical"
                  as="textarea"
                  minHeight="40px"
                  value={cohortDetails.description}
                  isDisabled={archivedCohort}
                  onChange={(e) => setCohortDetails({ ...cohortDetails, description: e.target.value })}
                />
              </Flex>
            </FormControl>
          </Stack>

          <FormControl>
            <Flex alignItems="center" zIndex={zIndices.multiSelect}>
              <FormLabel minWidth="120px" mb="0">
                {t("Years")}
              </FormLabel>

              <style>{SELECT_STYLE}</style>

              <NuMultiSelect
                allItems={yearOptions}
                selectedValues={selectedYears}
                searchPlaceholder="Search years"
                displayTextLens={(x: SelectOption<Year>) => x.text}
                valueLens={(x: SelectOption<Year>) => x.value}
                onAdd={(x: SelectOption<Year>) => {
                  setSelectedYears((prev) => prev.concat(x.value));
                }}
                onRemove={(x: Year) => {
                  setSelectedYears((prev) => prev.filter((e) => e !== x));
                }}
              />
            </Flex>
          </FormControl>
        </Stack>

        <Box backgroundColor="neutrals.20" m="sp16" p="sp8">
          <Text fontFamily="Poppins" fontSize="xl">
            {t("Groups")}
          </Text>
        </Box>

        <Grid gap="sp16" m="sp16" p="sp8" templateRows="1fr" templateColumns="1fr 1fr" id="groups">
          <GridItem>
            <Flex width="100%" direction={"column"}>
              {availableGroupHeader(availableGroups, visibleGroups)}
              <IdentitySearch<GroupUMS>
                minimumSearchLength={minimumSearchLength}
                setLoading={setIsSearching}
                disabled={archivedCohort}
                setState={setAvailableGroups}
                searchFn={search}
                placeholder="Search available groups"
                pageSize={GROUPS_PAGE_SIZE}
              />

              <Box maxHeight={MAX_TABLE_HEIGHT} overflow="scroll">
                <TableBasedPage
                  loaded={!isSearching}
                  columns={groupTableColumns.concat(buttonColumn)}
                  data={visibleGroups}
                  tableDataMap={availableTableDataMap}
                  showSearch={false}
                  tableMargin="sp0"
                  tablePadding="sp0"
                  tableTopMargin="sp0"
                  disableInfiniteScroll={true}
                />
              </Box>
            </Flex>
          </GridItem>

          <GridItem data-testid="selected-groups">
            <Flex width="100%" direction={"column"}>
              <Text fontFamily="Poppins" fontSize="s" mb="sp8">
                {t("Selected Groups")} ({filteredSelectedGroups.size})
              </Text>

              <SearchBox
                placeholder={t("Search selected groups")}
                value={selectedGroupsSearch}
                onChange={(e) => setSelectedGroupsSearch(e.target.value)}
                onClear={() => setSelectedGroupsSearch("")}
              />

              <Box maxHeight={MAX_TABLE_HEIGHT} overflow="scroll">
                <TableBasedPage
                  loaded={true}
                  columns={buttonColumn.concat(groupTableColumns)}
                  data={Array.from(filteredSelectedGroups.values())}
                  tableDataMap={selectedGroupsTableDataMap}
                  showSearch={false}
                  tableMargin="sp0"
                  tablePadding="sp0"
                  tableTopMargin="sp0"
                  disableInfiniteScroll={true}
                />
              </Box>
            </Flex>
          </GridItem>
        </Grid>

        <Box backgroundColor="neutrals.20" m="sp16" p="sp8">
          <Text fontFamily="Poppins" fontSize="xl">
            {t("Preview")}
          </Text>
        </Box>

        <Stack m="sp16" p="sp8">
          <Flex>
            <Text fontFamily="Poppins" fontSize="s" mb="sp8">
              {t("Qoria Users")} ({filteredUsers.length} of {cohortDetails.members.length})
            </Text>
          </Flex>
          <InlineNotification
            status="warning"
            notificationDescription={
              "Only the first 1000 users can be loaded. If a user does not appear when filtering please check their Cohort Details by going to Configuration > Users & Groups > Users and searching for the user."
            }
          />
          <FormControl>
            <SearchBox
              aria-label="Filter users"
              placeholder={t("Search Qoria users")}
              onChange={(e) => setFilteredUsers(filterUsers(e.target.value))}
              onClear={() => setFilteredUsers(filterUsers(""))}
            />
          </FormControl>
          <Flex data-testid="user-filter-results" direction="column">
            <TableBasedPage
              loaded={true}
              columns={userTableColumns}
              data={filteredUsers}
              tableDataMap={availableUserTableDataMap}
              showSearch={false}
              tableMargin="sp0"
              tablePadding="sp0"
              tableTopMargin="sp0"
              disableInfiniteScroll={true}
            />
          </Flex>
        </Stack>
        <Box backgroundColor="brand.100" p="sp24" flex="1">
          <Button
            data-testid="save-button"
            maxWidth="auto"
            disabled={saving || archivedCohort}
            isLoading={saving}
            variant="primary"
            onClick={() => void (async () => await handleSave())()}
          >
            {saving ? t("Saving...") : t("Save")}
          </Button>
        </Box>
      </Box>
    </CardBasedPage>
  );
};

export default ManageCohort;
